import type { JSX } from 'react';
import { z } from 'zod';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useZodForm } from '@/lib/forms/useZodForm';
import { toast } from '@/lib/toast';

const demoSchema = z.object({
  name: z
    .string()
    .min(3, 'Name must be at least 3 characters.')
    .max(50, 'Name must be 50 characters or fewer.'),
});

type DemoFormValues = z.infer<typeof demoSchema>;

export function DemoForm(): JSX.Element {
  const form = useZodForm({ schema: demoSchema, defaultValues: { name: '' } });

  const onSubmit = (values: DemoFormValues): void => {
    toast.success('Form submitted', {
      description: `Hello, ${values.name}! (Phase 3 proof-of-life — no backend call yet.)`,
    });
    form.reset();
  };

  const onInvalid = (errors: typeof form.formState.errors): void => {
    const firstField = Object.keys(errors)[0];
    const firstMessage =
      firstField !== undefined && firstField in errors
        ? errors[firstField as keyof typeof errors]?.message
        : undefined;
    toast.error('Please fix the errors below', {
      description:
        typeof firstMessage === 'string' ? firstMessage : 'One or more fields are invalid.',
    });
  };

  return (
    <form
      onSubmit={form.handleSubmit(onSubmit, onInvalid)}
      className="space-y-4 max-w-md"
      noValidate
    >
      <div className="space-y-2">
        <Label htmlFor="demo-name">Name</Label>
        <Input
          id="demo-name"
          placeholder="Aryan"
          aria-invalid={form.formState.errors.name ? 'true' : 'false'}
          {...form.register('name')}
        />
        {form.formState.errors.name && (
          <p className="text-xs text-destructive" role="alert">
            {form.formState.errors.name.message}
          </p>
        )}
      </div>
      <Button type="submit" disabled={form.formState.isSubmitting}>
        Submit
      </Button>
    </form>
  );
}
